import { useRef, useState } from "react";
import { processFile, type CompressedImage } from "../image";
import { apiReport } from "../api";
import { Screen, ScreenHeader, Display, Lede, Btn, SectionLabel, btnIcon } from "../ui/components";
import { Ico } from "../ui/Ico";
import { C, mono } from "../ui/tokens";

export function Camera({
  onScan, onCancel,
}: {
  onScan: (images: CompressedImage[]) => void;
  onCancel: () => void;
}) {
  const [images, setImages] = useState<CompressedImage[]>([]);
  const [busy, setBusy] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  async function onFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = "";
    if (files.length === 0) return;
    setBusy(true);
    try {
      // Process sequentially so memory pressure stays low on phones.
      const next: CompressedImage[] = [];
      for (const f of files) next.push(await processFile(f));
      setImages((prev) => [...prev, ...next]);
    } catch (err) {
      console.error(err);
      // This is the failure that used to leave nothing behind anywhere: the
      // request never happens, so the server never hears about it. iOS Safari
      // throws here for files it cannot read locally, for instance.
      void apiReport({
        kind: "process_file",
        detail: err instanceof Error ? `${err.name}: ${err.message}` : String(err),
        pages: files.map((f) => ({ mimeType: f.type || "(none)", bytes: f.size })),
      });
      alert("Datei konnte nicht verarbeitet werden. Der Fehler wurde gemeldet.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen>
      <ScreenHeader
        title="02 / Aufnehmen"
        right={
          <button onClick={onCancel} style={btnIcon} aria-label="Abbrechen">
            <Ico name="close" size={18} />
          </button>
        }
      />

      <Display>Quittung<br />erfassen</Display>
      <Lede>Mache ein Foto, wähle Bilder aus der Galerie oder lade ein PDF hoch. Mehrere Seiten sind möglich. Bei Restaurants kannst du den Kartenbeleg mit Trinkgeld als zusätzliche Seite hinzufügen.</Lede>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFilesSelected}
        style={{ display: "none" }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        onChange={onFilesSelected}
        style={{ display: "none" }}
      />

      {/* Viewfinder placeholder with corner brackets */}
      <div style={{
        marginTop: 24, height: 160, borderRadius: 20,
        border: `1px dashed ${C.border}`,
        background:
          "repeating-linear-gradient(135deg, rgba(255,255,255,0.02) 0 10px, transparent 10px 22px)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: 12, position: "relative", overflow: "hidden",
      }}>
        {([
          { top: 14, left: 14, sides: "tl" as const },
          { top: 14, right: 14, sides: "tr" as const },
          { bottom: 14, left: 14, sides: "bl" as const },
          { bottom: 14, right: 14, sides: "br" as const },
        ]).map((c, i) => (
          <div key={i} style={{
            position: "absolute", width: 22, height: 22,
            borderTop: c.sides.includes("t") ? `1.5px solid ${C.fg2}` : "none",
            borderBottom: c.sides.includes("b") ? `1.5px solid ${C.fg2}` : "none",
            borderLeft: c.sides.includes("l") ? `1.5px solid ${C.fg2}` : "none",
            borderRight: c.sides.includes("r") ? `1.5px solid ${C.fg2}` : "none",
            top: c.top, left: c.left, right: c.right, bottom: c.bottom,
          }} />
        ))}
        <Ico name="cam" size={30} color={C.fg3} />
        <div style={{ fontSize: 13, color: C.fg3 }}>Tippe unten, um eine Quelle zu wählen</div>
      </div>

      {/* Actions */}
      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Btn
            onClick={() => cameraInputRef.current?.click()}
            disabled={busy}
            icon={<Ico name="cam" size={18} color="#000" />}
          >
            Kamera
          </Btn>
          <Btn
            kind="glass"
            onClick={() => galleryInputRef.current?.click()}
            disabled={busy}
            icon={<Ico name="receipt" size={18} color={C.fg} />}
          >
            Galerie / PDF
          </Btn>
        </div>
        <Btn
          kind="glass"
          disabled={images.length === 0 || busy}
          onClick={() => onScan(images)}
          icon={images.length > 0 ? <Ico name="spark" size={16} color={C.fg} /> : undefined}
        >
          {images.length === 0 ? "Analysieren" : `Analysieren (${images.length})`}
        </Btn>
      </div>

      {/* Thumbnails */}
      {images.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <SectionLabel right={`${images.length} Seite${images.length === 1 ? "" : "n"}`}>
            Aufgenommen
          </SectionLabel>
          <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
            {images.map((img, i) => (
              <div key={i} style={{ position: "relative", flex: "0 0 auto" }}>
                {img.kind === "pdf" ? (
                  <div style={{
                    width: 80, height: 100, borderRadius: 12,
                    border: `1px solid ${C.border}`,
                    background: C.surface2,
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    gap: 6, padding: 6, textAlign: "center",
                  }}>
                    <Ico name="receipt" size={22} color={C.fg2} />
                    <div style={{
                      fontFamily: mono, fontSize: 10, color: C.fg3,
                      letterSpacing: "0.15em",
                    }}>PDF</div>
                    <div style={{
                      fontSize: 9, color: C.fg4, lineHeight: 1.2,
                      width: "100%",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }} title={img.name}>
                      {img.name.replace(/\.pdf$/i, "")}
                    </div>
                  </div>
                ) : (
                  <img
                    src={URL.createObjectURL(img.blob)}
                    alt={`Seite ${i + 1}`}
                    style={{
                      width: 80, height: 100, borderRadius: 12,
                      objectFit: "cover",
                      border: `1px solid ${C.border}`,
                      background: C.surface2,
                      display: "block",
                    }}
                  />
                )}
                <button
                  onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))}
                  aria-label="Seite entfernen"
                  style={{
                    position: "absolute", top: 4, right: 4,
                    width: 24, height: 24, borderRadius: 12,
                    background: "rgba(0,0,0,0.6)", color: "#fff",
                    border: "1px solid rgba(255,255,255,0.18)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", padding: 0,
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                  }}>
                  <Ico name="close" size={14} color="#fff" stroke={2} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 16, textAlign: "center" }}>
        <button onClick={onCancel} style={{
          background: "none", border: "none", cursor: "pointer",
          color: C.fg3, fontSize: 13, padding: 8,
        }}>Abbrechen</button>
      </div>
    </Screen>
  );
}
